import { replaceState } from '$app/navigation';
import { navigating, page } from '$app/state';
import { shouldShowEmailCapture } from '$lib/utils/emailCapture';
import { scrollLock } from '$lib/utils/scrollLock.svelte';
import { containsAnyParam } from '$lib/utils/url';
import { shoppingCart } from './shoppingCart.svelte';

interface EmailModalConfig {
	openDelay: number;
	ignoredParams: string[];
	modalParam: string;
}

export class EmailModal {
	#isShown = $state(false);
	#config: EmailModalConfig;
	#openTimeout: NodeJS.Timeout | null = null;

	constructor(config: Partial<EmailModalConfig> = {}) {
		this.#config = {
			openDelay: config.openDelay ?? 5000,
			ignoredParams: config.ignoredParams ?? [],
			modalParam: config.modalParam ?? 'newsletter'
		};

		if (
			page.url.searchParams.has(this.#config.modalParam) &&
			!this.#isShown &&
			sessionStorage.getItem(`${this.#config.modalParam}-modal`) !== 'success'
		) {
			this.#isShown = true;
			scrollLock.lock();
		}

		$effect.root(() => {
			$effect(() => {
				if (!navigating.to) return;

				if (sessionStorage.getItem(`${this.#config.modalParam}-modal`) === 'success') {
					this.hide();
					return;
				}

				if (navigating.to.url.searchParams.has(this.#config.modalParam) && !this.#isShown) {
					this.#isShown = true;
					scrollLock.lock();
					return;
				}

				if (!navigating.to.url.searchParams.has(this.#config.modalParam) && this.#isShown) {
					this.#isShown = false;

					if (!shoppingCart.isOpen) {
						scrollLock.unlock();
					}
					return;
				}
			});

			$effect(() => {
				if (navigating.from && this.#openTimeout) {
					clearTimeout(this.#openTimeout);
					this.#openTimeout = null;
				}
			});

			return () => {
				if (this.#openTimeout) {
					clearTimeout(this.#openTimeout);
					this.#openTimeout = null;
				}
			};
		});
	}

	get isShown() {
		return this.#isShown;
	}

	show() {
		if (this.#isShown || this.#openTimeout) return;

		if (!shouldShowEmailCapture()) return;

		const { ignoredParams = [], openDelay = 5000 } = this.#config;

		if (ignoredParams.length > 0 && containsAnyParam(page.url.search, ignoredParams)) return;

		this.#openTimeout = setTimeout(() => {
			this.#isShown = true;
			scrollLock.lock();
			this.#openTimeout = null;

			page.url.searchParams.set(this.#config.modalParam, 'open');
			replaceState(`?${page.url.searchParams.toString()}`, page.state);
		}, openDelay);
	}

	hide() {
		if (!this.#isShown) return;

		this.#isShown = false;

		if (!shoppingCart.isOpen) {
			scrollLock.unlock();
		}

		sessionStorage.setItem('mill-modal-dismissed', JSON.stringify({ name: this.#config.modalParam, reason: 'close' }));

		page.url.searchParams.delete(this.#config.modalParam);
		replaceState(
			page.url.searchParams.size > 0
				? `?${page.url.searchParams.toString()}`
				: window.location.pathname,
			page.state
		);
	}
}
